/**
 * One entry of the evidence-scenario catalogue ("VPOD Type"), as served by GET v2/EvidenceScenarios.
 * `applicability` arrives as its name and is an informational tag only, never a filter.
 *
 * Only `code` and `tileLabel` are read here; the rest is declared so the shape matches the wire.
 */
export interface EvidenceScenario {
	id: number;
	code: string;
	tileLabel: string;
	description: string;
	applicability: string;
	isActive: boolean;
}

/** code -> tileLabel, built from the catalogue served by GET v2/EvidenceScenarios. */
export type EvidenceScenarioLabelMap = Map<string, string>;

/**
 * Index a catalogue by its immutable `code`, which is what an attachment stores - never by `id`.
 * Entries missing a code or a label are skipped rather than mapped to a blank, so a malformed row
 * degrades to the raw-code fallback instead of rendering an empty label.
 */
export function buildEvidenceScenarioLabelMap(
	scenarios: { code?: string; tileLabel?: string }[] | null | undefined
): EvidenceScenarioLabelMap {
	const map: EvidenceScenarioLabelMap = new Map<string, string>();
	if (!scenarios) {
		return map;
	}
	for (const scenario of scenarios) {
		if (scenario && scenario.code && scenario.tileLabel) {
			map.set(scenario.code, scenario.tileLabel);
		}
	}
	return map;
}

/**
 * Resolve what an attachment row displays as its evidence scenario.
 *
 *   undeclared (null / undefined / blank) -> ''              nothing renders
 *   declared, code in the catalogue       -> the tile label
 *   declared, code NOT in the catalogue   -> the code itself
 *
 * The last case is load-bearing. Attachment reads are not filtered on active state, while the
 * catalogue serves active entries only, so retiring a scenario after capture leaves live rows whose
 * code has no label. Rendering those blank would make a declared attachment indistinguishable from
 * an undeclared one, which is the distinction this display exists to draw.
 *
 * Behaviour matches the Operations app's equivalent so the two surfaces cannot drift.
 */
export function resolveEvidenceScenarioLabel(
	code: string | null | undefined,
	labels: EvidenceScenarioLabelMap | null | undefined
): string {
	const declared = code ? code.trim() : '';
	if (!declared) {
		return '';
	}
	return (labels && labels.get(declared)) || declared;
}
